import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, Pill, DataTable, DecisionSummary } from '../components/ui'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror /api/v1/promotions/:id) ──────────────────────────────────
interface CoveredSku {
  name: string
  brand: string | null
  category: string
  price: number
  original_price: number | null
  discount_pct: number | null
  on_sale: boolean
  discount_label: string | null
}
interface OtherPromo {
  id: string
  text: string
  type: string | null
  category: string | null
  detected_at: string | null
}
interface PromoData {
  promo: {
    id: string
    text: string
    type: string | null
    category: string | null
    detected_at: string | null
    expired_at: string | null
    active: boolean
    source_url: string | null
    running_days: number | null
  }
  rival: { id: string; name: string; address: string | null; distance_mi: number | null; is_blocked: boolean }
  location: { id: string; name: string }
  covered_skus: CoveredSku[]
  other_promos: OtherPromo[]
  stats: { covered_count: number; largest_discount_pct: number | null; running_days: number | null; distance_mi: number | null }
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtMoney = (n: number) => '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 })
function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
// Free-text promo_type ("daily_deal", "happy_hour") → readable label.
function typeLabel(t: string | null): string {
  if (!t) return 'Promotion'
  return t.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
function titleCase(s: string | null): string {
  if (!s) return '—'
  return s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ─── Chrome ──────────────────────────────────────────────────────────────────
function Shell({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
        <button onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="15 18 9 12 15 6" /></svg>
          Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-2)' }} />
        <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em' }}>Promo Deep Dive</div>
      </div>
      {children}
    </div>
  )
}

function CenterState({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 40, textAlign: 'center' }}>
      {children}
    </div>
  )
}

function CardHead({ icon, title, sub }: { icon: React.ReactNode; title: string; sub?: string }) {
  return (
    <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
      {icon}
      <div style={{ fontSize: 12, fontWeight: 700 }}>{title}</div>
      {sub && <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>{sub}</div>}
    </div>
  )
}

function DetailItem({ label, value, sub, color }: { label: string; value: React.ReactNode; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 12px' }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: color || 'var(--text-1)' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function PromoDeepDive() {
  const navigate = useNavigate()
  const { promoId } = useParams<{ promoId: string }>()
  const [searchParams] = useSearchParams()
  const authFetch = useAuthFetch()

  const [data, setData] = useState<PromoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  const load = useCallback(() => {
    if (!promoId) return
    setLoading(true); setError(null); setNotFound(false)
    const locParam = searchParams.get('location_id')
    const qs = locParam ? `?location_id=${encodeURIComponent(locParam)}` : ''
    authFetch(`${API}/api/v1/promotions/${promoId}${qs}`)
      .then(async (r) => {
        if (r.status === 404) {
          const d = await r.json().catch(() => ({}))
          if (d.code === 'PROMO_NOT_FOUND' || d.code === 'PROMO_OUT_OF_SCOPE') { setNotFound(true); setData(null); return null }
          throw new Error(d.error || 'Not found')
        }
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: PromoData } | null) => { if (d) setData(d.data) })
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [promoId, searchParams, authFetch])

  useEffect(() => { load() }, [load])

  // ── Gates ─────────────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <Shell onBack={() => navigate(-1)}>
        <CenterState><div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>Pulling this promotion's latest detail…</div></CenterState>
      </Shell>
    )
  }
  if (error) {
    return (
      <Shell onBack={() => navigate(-1)}>
        <CenterState>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)' }}>Couldn't load this promotion.</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', maxWidth: 480 }}>{error}</div>
          <button onClick={load} style={{ marginTop: 6, fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
        </CenterState>
      </Shell>
    )
  }
  if (notFound || !data) {
    return (
      <Shell onBack={() => navigate(-1)}>
        <CenterState>
          <div style={{ fontFamily: 'var(--display)', fontSize: 22, color: 'var(--text-1)' }}>Promotion not available</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 460, lineHeight: 1.55 }}>
            This promotion either no longer exists or belongs to a rival you aren't actively tracking. Track that rival to see their promotions here.
          </div>
          <button onClick={() => navigate('/today')} style={{ marginTop: 6, fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-1)', cursor: 'pointer' }}>Back to Today</button>
        </CenterState>
      </Shell>
    )
  }

  const { promo, rival, location, covered_skus, other_promos, stats } = data
  const statusWord = promo.active ? 'Active' : 'Ended'
  const largest = stats.largest_discount_pct
  const deepestMarkdownCount = largest == null
    ? 0
    : covered_skus.filter((sku) => sku.discount_pct === largest).length
  const priceValues = covered_skus.map((sku) => sku.price).filter(Number.isFinite)
  const promoPriceRange = priceValues.length
    ? `${fmtMoney(Math.min(...priceValues))}–${fmtMoney(Math.max(...priceValues))}`
    : null
  const priceCompareParams = new URLSearchParams({ location_id: location.id })
  if (promo.category) priceCompareParams.set('category', promo.category)

  const skuColumns: Column<CoveredSku>[] = [
    {
      key: 'name', header: 'SKU', mono: false,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{r.name}</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>
            {[r.brand, r.category].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'original_price', header: 'Regular', align: 'right',
      render: (r) => r.original_price != null
        ? <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>{fmtMoney(r.original_price)}</span>
        : <span style={{ color: 'var(--text-3)' }}>—</span>,
    },
    {
      key: 'price', header: 'On promo', align: 'right',
      render: (r) => <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmtMoney(r.price)}</span>,
    },
    {
      key: 'discount_pct', header: 'Discount', align: 'right',
      render: (r) => r.discount_pct != null
        ? <Pill tone="danger" variant="chip">−{r.discount_pct}%</Pill>
        : <span style={{ color: 'var(--text-3)' }}>—</span>,
    },
  ]

  return (
    <Shell onBack={() => navigate(-1)}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}>

        <DecisionSummary
          className="promo-deep-dive-summary"
          question="What promotion is active, and what does the evidence support?"
          priority="watch"
          answer={`${rival.name} has ${promo.active ? 'an active offer' : 'an offer that has ended'}: ${promo.text}`}
          change={`First detected ${fmtDate(promo.detected_at)}${promo.running_days != null ? ` and observed for ${promo.running_days} ${promo.running_days === 1 ? 'day' : 'days'}` : ''}. The latest rival menu shows ${stats.covered_count} current ${titleCase(promo.category).toLowerCase()} markdown${stats.covered_count === 1 ? '' : 's'}${largest != null ? `; ${deepestMarkdownCount} reach${deepestMarkdownCount === 1 ? 'es' : ''} ${largest}% off` : ''}.`}
          impact={stats.covered_count > 0
            ? `Those markdowns span ${promoPriceRange}, but CannaSpy cannot yet attribute them to this offer or match them to ${location.name}'s catalog. Product overlap and commercial exposure are unavailable, so the evidence does not support an automatic price response.`
            : `No product-level markdowns are visible on the latest rival menu. Product overlap and commercial exposure are unavailable, so the evidence does not support an automatic price response.`}
          comparison={{ baseline: "the rival's listed regular prices", period: 'latest menu' }}
          provenance={{ updatedAt: 'with latest menu observation', source: 'rival menu', confidence: 'unavailable' }}
          action={(
            <button
              className="btn btn-primary"
              data-testid="promo-compare-category"
              onClick={() => navigate(`/prices?${priceCompareParams.toString()}`)}
            >
              Compare {promo.category ? titleCase(promo.category).toLowerCase() : 'category'} prices →
            </button>
          )}
        >
          <div style={{ display: 'flex', gap: 7, marginTop: 12, flexWrap: 'wrap' }}>
            <Pill tone="warning" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>{typeLabel(promo.type)}</Pill>
            {stats.covered_count > 0 && (
              <Pill tone="danger" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>{stats.covered_count} current markdown{stats.covered_count === 1 ? '' : 's'}</Pill>
            )}
            {rival.distance_mi != null && (
              <Pill tone="neutral" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>{rival.distance_mi} mi away</Pill>
            )}
            <Pill tone="neutral" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>{statusWord} · detected {fmtDate(promo.detected_at)}</Pill>
          </div>
        </DecisionSummary>

        {/* DETAIL COLUMN — one dominant promotion-detail surface */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>

            {/* Promotion details */}
            <Card>
              <CardHead
                icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--warm)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
                title="Promotion Details"
                sub={rival.name}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '14px 16px' }}>
                <DetailItem label="Type" value={typeLabel(promo.type)} sub="As classified from the rival's live menu" />
                <DetailItem label="Category" value={titleCase(promo.category)} sub={promo.category ? 'Promo scope' : 'Not specified'} />
                <DetailItem label="First detected" value={fmtDate(promo.detected_at)} sub={promo.running_days != null ? `${promo.running_days} ${promo.running_days === 1 ? 'day' : 'days'} ago` : undefined} />
                <DetailItem
                  label="Status"
                  value={promo.active ? '● Active' : '● Ended'}
                  sub={promo.active ? 'Seen on their latest menu' : promo.expired_at ? `Ended ${fmtDate(promo.expired_at)}` : 'No longer on menu'}
                  color={promo.active ? 'var(--warm)' : 'var(--text-3)'}
                />
                <DetailItem label="Rival" value={rival.name} sub={rival.address || undefined} />
                <DetailItem
                  label="Source"
                  value={promo.source_url
                    ? <a href={promo.source_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Rival menu ↗</a>
                    : 'Rival menu'}
                  sub="Publicly available market data"
                />
              </div>
            </Card>

            {/* Covered SKUs */}
            <Card>
              <CardHead
                icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h6" /></svg>}
                title="Current category markdowns at this rival"
                sub={promo.category ? `${titleCase(promo.category)} · not attributed to this offer` : 'Not attributed to this offer'}
              />
              <DataTable
                columns={skuColumns}
                rows={covered_skus}
                rowKey={(r) => r.name}
                empty={
                  <div style={{ padding: '8px 4px', lineHeight: 1.5 }}>
                    No product-level markdowns detected on {rival.name}'s menu right now.
                    <br />We'll surface them here the moment their prices drop.
                  </div>
                }
              />
            </Card>

            {/* Other active promotions */}
            <Card>
              <CardHead
                icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.5" /></svg>}
                title={`Other active promotions at ${rival.name}`}
                sub={`${other_promos.length} tracked`}
              />
              <div style={{ padding: '8px 16px' }}>
                {other_promos.length === 0 ? (
                  <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-3)' }}>
                    This is the only active promotion we're tracking at {rival.name}.
                  </div>
                ) : (
                  other_promos.map((o) => (
                    <div key={o.id}
                      onClick={() => navigate(`/promotions/${o.id}?location_id=${location.id}`)}
                      style={{ display: 'grid', gridTemplateColumns: '70px 90px 1fr', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    >
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>{fmtDate(o.detected_at)}</span>
                      <Pill tone="warning" variant="chip">{typeLabel(o.type)}</Pill>
                      <span style={{ fontSize: 11.5, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.text}</span>
                    </div>
                  ))
                )}
              </div>
            </Card>
        </div>
      </div>
    </Shell>
  )
}
